import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Breadcrumbs, CircularProgress, Link as MuiLink, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";
import { DsokManagementListToolbar } from "app/pages/customers/dsok/components/DsokManagementListToolbar";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  createPosWorkspacesDocument,
  getPosWorkspacesDocument,
  isPosWorkspacesDocumentPersisted,
  updatePosWorkspacesDocument,
} from "app/services/api/accessSystemPosWorkspaces";
import { getLoggedInCustomerId } from "app/pages/customers/dsok/access-system/accessSystemLoggedInCustomer";
import { normalizeWorkspacesForUi } from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import PosWorkspaceListItem from "./workspaces/PosWorkspaceListItem";
import PosWorkspaceFormModal from "./workspaces/PosWorkspaceFormModal";

const POS_WORKSPACES_SEARCH_PAGE = "DsokPosWorkspaces";

function filterWorkspaces(rows, queryRaw) {
  const q = String(queryRaw ?? "")
    .trim()
    .toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((row) => {
    const name = String(row.name ?? "").toLowerCase();
    const uuid = String(row.uuid ?? "").toLowerCase();
    return name.includes(q) || uuid.includes(q);
  });
}

function hasDuplicateUuids(data) {
  const seen = new Set();
  for (const w of data || []) {
    const u = String(w?.uuid ?? "").trim();
    if (!u) continue;
    if (seen.has(u)) return true;
    seen.add(u);
  }
  return false;
}

const emptyWorkspace = () => ({
  uuid:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `ws-${Date.now()}`,
  name: "",
  features: {},
  pos: {},
  devices: [],
});

const DsokPosWorkspacesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const customerId = getLoggedInCustomerId(getAuthUser);

  const title = t("pages.pos.workspacesTitle");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentPersisted, setDocumentPersisted] = useState(false);

  const [workspacesData, setWorkspacesData] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalWorkspace, setModalWorkspace] = useState(null);

  const applyLoadedDocument = useCallback((doc) => {
    const normalized = normalizeWorkspacesForUi(doc);
    setWorkspacesData(Array.isArray(normalized.data) ? normalized.data : []);
    setDocumentPersisted(isPosWorkspacesDocumentPersisted(doc));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const doc = await getPosWorkspacesDocument();
      applyLoadedDocument(doc && typeof doc === "object" ? doc : {});
      setLoadError(false);
    } catch (e) {
      console.error(e);
      setLoadError(true);
      setWorkspacesData([]);
      setDocumentPersisted(false);
    } finally {
      setLoading(false);
    }
  }, [applyLoadedDocument]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(
    () => filterWorkspaces(workspacesData, searchQuery),
    [workspacesData, searchQuery],
  );

  const existingUuidsForModal = useMemo(
    () =>
      workspacesData
        .map((w) => String(w?.uuid ?? "").trim())
        .filter(Boolean),
    [workspacesData],
  );

  const openCreate = () => {
    setModalWorkspace(emptyWorkspace());
    setModalOpen(true);
  };

  const goToEditWorkspace = (row) => {
    const u = String(row?.uuid ?? "").trim();
    if (!u) return;
    navigate(`/customer/dsok/pos/workspaces/${encodeURIComponent(u)}`);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalWorkspace(null);
  };

  const persistWorkspacesList = useCallback(
    async (dataPayload) => {
      if (customerId == null || String(customerId).trim() === "") {
        enqueueSnackbar(t("pages.pos.workspaces.loadMissingCustomer"), {
          variant: "error",
        });
        throw new Error("missing customer");
      }

      for (const w of dataPayload) {
        const name = String(w?.name ?? "").trim();
        const uuid = String(w?.uuid ?? "").trim();
        if (!name || !uuid) {
          enqueueSnackbar(t("pages.pos.workspaces.validation.incompleteRows"), {
            variant: "warning",
          });
          throw new Error("incomplete");
        }
      }

      if (hasDuplicateUuids(dataPayload)) {
        enqueueSnackbar(
          t("pages.pos.workspaces.validation.duplicateUuidGlobal"),
          { variant: "error" },
        );
        throw new Error("duplicate uuid");
      }

      const data = dataPayload.map((w) => ({ ...w }));

      if (!documentPersisted) {
        await createPosWorkspacesDocument({
          customerId,
          data,
        });
        setDocumentPersisted(true);
      }
      await updatePosWorkspacesDocument(customerId, {
        data,
      });
      enqueueSnackbar(t("pages.pos.workspaces.saveSuccess"), {
        variant: "success",
      });
      await load();
    },
    [
      customerId,
      documentPersisted,
      enqueueSnackbar,
      load,
      t,
    ],
  );

  const handleModalSave = async (workspace) => {
    const next = [...workspacesData, workspace];

    setSaving(true);
    try {
      await persistWorkspacesList(next);
      closeModal();
    } catch (_e) {
      /* snackbars + errors from persistWorkspacesList */
    } finally {
      setSaving(false);
    }
  };

  const searchPlaceholder = t("pages.pos.searchPlaceholder");
  const addTooltip = t("pages.pos.users.listAddTooltip", {
    defaultValue: t("pages.pos.addWorkspace"),
  });

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <Alert severity="error">{t("pages.pos.workspaces.loadError")}</Alert>
        </Box>
      );
    }

    if (!workspacesData.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    if (!filteredRows.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    return (
      <>
        {filteredRows.map((row, idx) => {
          const key = row?.uuid != null ? String(row.uuid) : `row-${idx}`;
          return (
            <PosWorkspaceListItem
              key={key}
              row={row}
              onEdit={goToEditWorkspace}
            />
          );
        })}
      </>
    );
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <MuiLink
          component={Link}
          to="/customer/dsok"
          color="inherit"
          underline="hover"
        >
          {t("pages.pos.breadcrumbCustomer")}
        </MuiLink>
        <Typography color="text.secondary">{t("sidebar.menu.pos")}</Typography>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h2" mb={3}>
        {title}
      </Typography>

      <DsokManagementListToolbar
        searchPage={POS_WORKSPACES_SEARCH_PAGE}
        onSearchChange={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        addTooltip={addTooltip}
        onAddClick={openCreate}
        addAriaLabel={addTooltip}
      />

      {renderMainContent()}

      <PosWorkspaceFormModal
        open={modalOpen}
        mode="create"
        workspace={modalWorkspace}
        existingUuids={existingUuidsForModal}
        onClose={closeModal}
        onSave={handleModalSave}
        saveInProgress={saving}
      />
    </React.Fragment>
  );
};

export default DsokPosWorkspacesPage;
